// hooks/useAuth.ts
import { useEffect } from "react";
import { acquireAuthLock, releaseAuthLock } from '@/lib/authLock';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { CONSENT_COOKIE, deleteCookie, deleteGuestConsent } from '@/lib/cookies';
import {
  getAccessToken,
  setAccessToken,
  clearAuthState,
  getOrCreateDeviceId,
  setSessionId,
  getSessionId,
  broadcastLogout,
  emitLocalLogoutMarker,
  getClientPublicIp,
} from "@/lib/authToken";
// (legacy alias import removed)

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const endpoint = (path: string) => `${API_BASE_URL}${path}`;

// Minimal user profile shape (extend as needed)
export interface UserProfile {
  id?: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  credits?: number;
  [k: string]: any;
}

// ---------- AUTH (NEW FLOW) ----------
// Registration (kept similar; adjust endpoints if backend changes)
const sendCode = async (email: string) => {
  const res = await apiRequest("POST", "/api/auth/send-code", { email });
  return res.json();
};

const verifyCode = async (data: { email: string; code: string }) => {
  const res = await apiRequest("POST", "/api/auth/verify-code", data);
  return res.json();
};

const completeRegistration = async (data: any) => {
  // Include device + network info so backend can trust this device immediately
  const device_id = getOrCreateDeviceId();
  const client_public_ip = await getClientPublicIp();
  const payload: Record<string, any> = {
    email: data.email,
    session_token: data.sessionToken,
    first_name: data.firstName,
    last_name: data.lastName,
    password: data.password,
    confirm_password: data.confirmPassword,
    accepted_terms_of_use: data.acceptedTermsOfUse,
    accepted_privacy_policy: data.acceptedPrivacyPolicy,
    accepted_refund_policy: data.acceptedRefundPolicy,
    accepted_risk_disclosure: data.acceptedRiskDisclosure,
    device_id,
  };
  if (data.legalAcceptanceMeta) {
    payload.legal_acceptance = {
      accepted_at: data.legalAcceptanceMeta.accepted_at,
      documents: data.legalAcceptanceMeta.documents,
    };
  }
  if (client_public_ip) payload.client_public_ip = client_public_ip;
  const res = await apiRequest("POST", "/api/auth/complete-registration", payload);
  // We intentionally do NOT set access/session tokens here to force explicit login afterwards.
  return res.json().catch(() => ({}));
};

// Login now includes device_id + remember_me
interface LoginPayload { email: string; password: string; remember_me: boolean; }
interface LoginResponse {
  access_token?: string;
  session_id?: number;
  session_status?: 'active' | 'pending' | string;
  requires_otp?: boolean;
}

const loginUser = async (data: LoginPayload): Promise<LoginResponse> => {
  const device_id = getOrCreateDeviceId();
  const client_public_ip = await getClientPublicIp();
  const res = await fetch(endpoint('/api/auth/login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      email: data.email,
      password: data.password,
      remember_me: data.remember_me,
      device_id,
      client_public_ip,
    }),
  });
  const rawText = await res.text();
  let json: any = {};
  try { json = rawText ? JSON.parse(rawText) : {}; } catch { /* non-json */ }
  if (import.meta.env.DEV) {
    // Debug log
    // eslint-disable-next-line no-console
    console.log('[loginUser] status', res.status, 'response:', json || rawText);
  }
  // Some backends may return 401 for a pending (new device) session instead of 200.
  if (!res.ok) {
    if (
      res.status === 401 &&
      (json?.session_status === 'pending' || json?.requires_otp) &&
      json?.session_id
    ) {
      // Treat this as a valid pending session (no access token yet)
      setSessionId(json.session_id);
      return json;
    }
    const msg = json?.detail || json?.message || rawText || 'Unauthorized';
    throw new Error(msg);
  }
  if (json.access_token) setAccessToken(json.access_token);
  if (json.session_id) setSessionId(json.session_id);
  return json;
};

// OTP verify for new device
const verifyDeviceOtp = async (data: { session_id: number; code: string }) => {
  const res = await fetch(endpoint('/api/auth/device-otp/verify'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Some implementations might respond 401 even though OTP accepted—defensive check
    if (res.status === 401 && json?.access_token) {
      setAccessToken(json.access_token);
      if (json.session_id) setSessionId(json.session_id);
      return json;
    }
    throw new Error(`${res.status}: ${JSON.stringify(json)}`);
  }
  if (json.access_token) setAccessToken(json.access_token);
  if (json.session_id) setSessionId(json.session_id);
  return json;
};

// Resend device OTP
const resendDeviceOtp = async (session_id: number) => {
  const res = await fetch(endpoint('/api/auth/device-otp/send'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ session_id }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(`${res.status}: ${JSON.stringify(json)}`);
  return json;
};

const logoutRequest = async () => {
  await fetch(endpoint('/api/auth/logout'), { method: 'POST', credentials: 'include' });
  return true;
};

const forgotPassword = async (email: string) => {
  const res = await apiRequest("POST", "/api/auth/forgot-password", { email });
  return res.json();
};

const resetPassword = async (data: { email: string; code: string; newPassword: string }) => {
  const res = await apiRequest("POST", "/api/auth/reset-password", data);
  return res.json();
};

// ---------- USER ----------
const getUserProfile = async (): Promise<UserProfile> => {
  const res = await apiRequest("GET", "/api/user/profile");
  return res.json();
};

const getDetailedProfile = async () => {
  const res = await apiRequest("GET", "/api/user/profile/detailed");
  return res.json();
};

const updateUserProfile = async (data: any) => {
  const res = await apiRequest("PUT", "/api/user/profile", data);
  return res.json();
};

const getUserStats = async () => {
  const res = await apiRequest("GET", "/api/user/stats");
  return res.json();
};

// Sessions management
interface SessionInfo {
  id: number;
  device_id: string;
  browser?: string;
  os?: string;
  location_country?: string;
  location_city?: string;
  last_seen_at?: string;
  expires_at?: string;
  status?: string;
  remember_me?: boolean;
}

const listSessions = async (): Promise<SessionInfo[]> => {
  const res = await apiRequest('GET', '/api/user/sessions');
  return res.json();
};

const revokeSession = async (id: number) => {
  const res = await apiRequest('DELETE', `/api/user/sessions/${id}`);
  return res.text();
};

// ---------- HOOK ----------
export function useAuth() {
  const queryClient = useQueryClient();
  const PROFILE_KEY = ["/api/user/profile"] as const;

  const { data: user, isLoading } = useQuery<UserProfile>({
    queryKey: PROFILE_KEY,
    queryFn: getUserProfile,
    retry: false,
    enabled: !!getAccessToken(),
  });

  const sessionsQuery = useQuery<SessionInfo[]>({
    queryKey: ['/api/user/sessions'],
    queryFn: listSessions,
    enabled: !!getAccessToken(),
    refetchInterval: 60_000,
  });

  const sendCodeMutation = useMutation({ mutationFn: sendCode });
  const verifyCodeMutation = useMutation({ mutationFn: verifyCode });
  const completeRegistrationMutation = useMutation({ mutationFn: completeRegistration });
  const loginMutation = useMutation({
    mutationFn: async (p: { email: string; password: string; rememberMe?: boolean }) => {
      const deviceOwner = getOrCreateDeviceId();
      const got = acquireAuthLock('login', deviceOwner, 15_000);
      if (!got) {
        // Wait for other tab to finish (could enhance with waitForAuthLock)
        throw new Error('Another login in progress');
      }
      try {
        return await loginUser({ email: p.email, password: p.password, remember_me: !!p.rememberMe });
      } finally {
        releaseAuthLock('login', deviceOwner);
      }
    },
    onSuccess: async (resp: LoginResponse) => {
      if (resp.session_status === 'pending' || resp.requires_otp) {
        return;
      }
      if (resp.access_token) queryClient.invalidateQueries({ queryKey: PROFILE_KEY });
      // Do not auto-create/overwrite consent on login — user must explicitly choose via banner/settings.
    },
  });

  const verifyDeviceOtpMutation = useMutation({
    mutationFn: (p: { code: string }) => {
      const sid = getSessionId();
      if (!sid) return Promise.reject(new Error('Missing session id'));
      return verifyDeviceOtp({ session_id: sid, code: p.code });
    },
    onSuccess: async (resp) => {
      if (resp.access_token) {
        queryClient.invalidateQueries({ queryKey: PROFILE_KEY });
        queryClient.invalidateQueries({ queryKey: ['/api/user/sessions'] });
        // Do not auto-create/overwrite consent on OTP success either.
      }
    },
  });

  const resendDeviceOtpMutation = useMutation({
    mutationFn: () => {
      const sid = getSessionId();
      if (!sid) return Promise.reject(new Error('Missing session id'));
      return resendDeviceOtp(sid);
    },
  });

  const revokeSessionMutation = useMutation({
    mutationFn: revokeSession,
    onSuccess: (_, sid) => {
      // If we revoked our own session -> force logout
      if (sid === getSessionId()) {
        clearAuthState();
        broadcastLogout();
      }
      queryClient.invalidateQueries({ queryKey: ['/api/user/sessions'] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: logoutRequest,
    onSettled: () => {
      clearAuthState();
      broadcastLogout();
      emitLocalLogoutMarker();
      queryClient.clear();
      // Remove guest consent so next visitor sees the banner (Phase I reset)
      try { deleteCookie(CONSENT_COOKIE); } catch {}
      try { deleteGuestConsent(); } catch {}
    },
  });

  // Backwards compatibility placeholders (legacy email verification)
  const resendCodeMutation = { mutateAsync: async () => Promise.resolve(), isPending: false } as any;
  const forgotPasswordMutation = useMutation({ mutationFn: forgotPassword });
  const resetPasswordMutation = useMutation({ mutationFn: resetPassword });
  const updateProfileMutation = useMutation({
    mutationFn: updateUserProfile,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROFILE_KEY }),
  });

  const detailedProfileQuery = useQuery({
    queryKey: ["/api/user/profile/detailed"],
    queryFn: getDetailedProfile,
    enabled: !!getAccessToken(),
  });

  const userStatsQuery = useQuery({
    queryKey: ["/api/user/stats"],
    queryFn: getUserStats,
    enabled: !!getAccessToken(),
  });

  // Optimistic credits delta
  const updateUserCredits = (delta: number) => {
    queryClient.setQueryData<UserProfile | undefined>(PROFILE_KEY, (prev) => {
      if (!prev) return prev;
      const current = typeof prev.credits === "number" ? prev.credits : 0;
      const next = Math.max(0, current + delta);
      return { ...prev, credits: next };
    });
  };

  const patchUser = (patch: Partial<UserProfile>) => {
    queryClient.setQueryData<UserProfile | undefined>(PROFILE_KEY, (prev) =>
      prev ? { ...prev, ...patch } : prev
    );
  };

  const logout = () => {
    logoutMutation.mutate();
  };

  // Hook to re-render when token changes (subscribe pattern)
  useEffect(() => {
    // no-op; presence of access token already drives queries enabled
  }, [getAccessToken()]);

  return {
    user,
    isLoading,
  isAuthenticated: !!user && !!getAccessToken(),

  // New device/session management
  verifyDeviceOtpMutation,
  resendDeviceOtpMutation,
  revokeSessionMutation,
  sessionsQuery,
  logoutMutation,

    sendCodeMutation,
    verifyCodeMutation,
    completeRegistrationMutation,
    loginMutation,
    resendCodeMutation,
    forgotPasswordMutation,
    resetPasswordMutation,
    updateProfileMutation,

    detailedProfileQuery,
    userStatsQuery,

    updateUserCredits,
    patchUser,
    logout,
  currentSessionId: getSessionId(),
  };
}